import { Player } from "@blings/blings-player";

export default class MuterSingleton {
  private static _instance: MuterSingleton;
  private _shouldMute: boolean = false;
  private _currentMediaElements: HTMLMediaElement[] = [];
  private _originalMutedDescriptor: PropertyDescriptor | undefined;

  public isMuting(): boolean {
    return this._shouldMute;
  }

  public static get instance(): MuterSingleton {
    if (!MuterSingleton._instance) {
      MuterSingleton._instance = new MuterSingleton();
    }
    return MuterSingleton._instance;
  }

  private constructor() {
    // Store the original muted property descriptor
    this._originalMutedDescriptor = Object.getOwnPropertyDescriptor(
      HTMLMediaElement.prototype,
      "muted"
    );

    // Override the muted setter to prevent unmuting when global mute is active
    if (this._originalMutedDescriptor) {
      const self = this;
      const originalDescriptor = this._originalMutedDescriptor;

      Object.defineProperty(HTMLMediaElement.prototype, "muted", {
        get: function () {
          return originalDescriptor.get?.call(this);
        },
        set: function (value: boolean) {
          // If global mute is active and trying to unmute, force muted to true
          if (self._shouldMute && value === false) {
            originalDescriptor.set?.call(this, true);
          } else {
            originalDescriptor.set?.call(this, value);
          }
        },
        configurable: true,
        enumerable: true,
      });
    }

    // MutationObserver to mute new media elements when they are added to the DOM
    const observer = new MutationObserver((mutations) => {
      if (!this._shouldMute) return;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLMediaElement) {
            node.muted = true;
          }
          if (node instanceof HTMLElement) {
            node.querySelectorAll("video, audio").forEach((el) => {
              (el as HTMLMediaElement).muted = true;
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  public toggleMute(): void {
    if (this._shouldMute) {
      this.unmuteMedia();
    } else {
      this.muteMedia();
    }
  }

  public static getInstance(): MuterSingleton {
    if (!MuterSingleton.instance) {
      MuterSingleton._instance = new MuterSingleton();
    }
    return MuterSingleton.instance;
  }

  public muteMedia(): void {
    this._shouldMute = true;

    // Get the player to access both BGMedia and overlay audio elements
    const player = (window as any).p as Player;

    // Mute all tracked media elements
    this._currentMediaElements.forEach((element) => {
      element.muted = true;
    });

    // Mute all video/audio elements in the document
    document.querySelectorAll("video, audio").forEach((el) => {
      (el as HTMLMediaElement).muted = true;
    });

    // Also explicitly mute overlay audio elements if they exist
    if (player?.overlayMediaHandler?.overlayElements?.audios) {
      player.overlayMediaHandler.overlayElements.audios.forEach(
        (audioElement: HTMLAudioElement) => {
          audioElement.muted = true;
        }
      );
    }
  }

  public unmuteMedia(): void {
    this._shouldMute = false;

    // Get the player and check if the BGMedia elements are muted from After Effects or not before unmuting
    const player = (window as any).p as Player;
    const bgMediaElements = player?.bgMedia;
    this._currentMediaElements.forEach((element) => {
      if (bgMediaElements) {
        const bgMediaElement = bgMediaElements.find(
          (bgMediaElement) => bgMediaElement.mediaEl?.src === element.src
        );
        if (bgMediaElement && bgMediaElement.origin.mutedFromAe) return;
      }
      element.muted = false;
    });

    // Also explicitly unmute overlay audio elements if they exist
    if (player?.overlayMediaHandler?.overlayElements?.audios) {
      player.overlayMediaHandler.overlayElements.audios.forEach(
        (audioElement: HTMLAudioElement) => {
          audioElement.muted = false;
        }
      );
    }
  }
}
